import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithRef } from 'react'
import s from './Button.module.css'

/**
 * `cva` compose des classes de **module CSS** — ce n'est pas une bibliothèque Tailwind.
 * Son intérêt ici est le typage : `VariantProps` dérive les props du composant depuis
 * cette déclaration, donc un ton inexistant ne compile pas.
 */
const button = cva(s.base, {
  variants: {
    tone: {
      brand: s.toneBrand,
      neutral: s.toneNeutral,
      danger: s.toneDanger,
      ghost: s.toneGhost,
      /** `.btn-accent` du prototype back-office. */
      accent: s.toneAccent,
    },
    size: {
      sm: s.sizeSm,
      md: null,
      lg: s.sizeLg,
      /**
       * `.open-actions .btn` du prototype terrain : les gestes empilés de la pré-fiche
       * d'ouverture, pleine largeur, le verbe à gauche et sa mention à droite.
       */
      action: s.sizeAction,
    },
    fullWidth: {
      true: s.fullWidth,
    },
  },
  defaultVariants: { tone: 'neutral', size: 'md' },
})

export type ButtonProps = ComponentPropsWithRef<'button'> &
  VariantProps<typeof button> & {
    /**
     * Le geste est parti et n'a pas répondu — un téléchargement, un archivage. Le bouton se
     * désactive (un second clic doublerait l'envoi), l'annonce (`aria-busy`) et prend le
     * curseur d'attente. **Le libellé reste à l'appelant** : « Téléchargement… » se dit mieux
     * dans les mots de l'écran que dans ceux du composant.
     */
    pending?: boolean
  }

export function Button({
  tone,
  size,
  fullWidth,
  pending = false,
  disabled,
  className,
  type,
  ...props
}: ButtonProps) {
  return (
    <button
      // Sans `type` explicite, un bouton dans un formulaire vaut `submit` — cause
      // classique d'envois involontaires. Le défaut du design system est `button`.
      type={type ?? 'button'}
      className={button({ tone, size, fullWidth, className })}
      disabled={disabled === true || pending}
      aria-busy={pending || undefined}
      data-pending={pending ? '' : undefined}
      {...props}
    />
  )
}
